import { Button } from '@progress/kendo-react-buttons';
import { Window, WindowActionsBar } from '@progress/kendo-react-dialogs';
import { Loader } from '@progress/kendo-react-indicators';
import React from 'react';
import { useAppDispatch } from '../../app/hooks/store-hooks';
import { useSafeEffect } from '../../app/hooks/use-safe-effect';
import { updateDrawingObject } from '../../app/state/slices/drawing-objects-slice';
import { drawingObjectSetIdleWorkspace } from '../../app/state/slices/workspace-slice';
import useDrawingObjectLayer from './hooks/useDrawingObjectLayer';
import { DrawingObject } from './types';

const DrawingObjectGraphicEdit = (props: { object: DrawingObject }) => {
    const layer = useDrawingObjectLayer();
    const dispatch = useAppDispatch();
    const [busy, setBusy] = React.useState(false);

    const handleSave = async () => {
        setBusy(true);
        const newGeometry = layer.graphicEditApply();
        const edited = { ...props.object, geometry: newGeometry } as DrawingObject;
        try {
            await dispatch(updateDrawingObject(edited)).unwrap();
        } catch {
            return;
        } finally {
            setBusy(false);
        }

        dispatch(drawingObjectSetIdleWorkspace());
    };

    const handelCancel = () => {
        layer.graphicEditCancel();
        dispatch(drawingObjectSetIdleWorkspace());
    };

    const onKeyDown = React.useCallback((e: KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSave();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            dispatch(drawingObjectSetIdleWorkspace());
        }
    }, []);

    React.useEffect(() => {
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, []);

    useSafeEffect(() => {
        layer.beginEdit(props.object);
        layer.graphicEditStart();
        return () => {
            layer.endEdit();
        };
    }, []);

    return (
        <Window
            doubleClickStageChange={false}
            minimizeButton={() => <></>}
            maximizeButton={() => <></>}
            closeButton={() => <></>}
            initialHeight={0}
            resizable={false}
            title={'Edit drawing object'}>
            <div>
                <strong>{props.object.name}</strong>
            </div>

            <WindowActionsBar layout={'stretched'}>
                <Button themeColor={'primary'} onClick={handleSave} disabled={busy}>
                    {busy && <Loader type={'infinite-spinner'} size="small" className="me-2" />}
                    <span>Save</span>
                </Button>
                <Button fillMode={'outline'} onClick={handelCancel} disabled={busy}>
                    Cancel
                </Button>
            </WindowActionsBar>
        </Window>
    );
};

export default DrawingObjectGraphicEdit;
